import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { DeleteOutlined, FileOutlined, UploadOutlined, CameraOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, message, Modal, Row, Spin, Avatar } from "antd";
import { AiFillEye } from "react-icons/ai";
import Api from "../../../services/api";
import PropTypes from 'prop-types';
import { useTheme } from '../../../contexts/ThemeContext';
import Loading from '../../loading/Loading';

const areFilesEquivalent = (currentFiles, nextFiles) => {
  if (currentFiles.length !== nextFiles.length) return false;

  return currentFiles.every((currentFile, index) => {
    const nextFile = nextFiles[index];
    return currentFile?.id === nextFile?.id && currentFile?.preview === nextFile?.preview;
  });
};

const FileUpload = ({ 
  onFileUpload, 
  preFilesIds = [], 
  inputType = "images",
  style,
  className,
  userId = "",
  baseUrl = "",
  user = null,
  hideUploadWhenHasFiles = false,
  icon = <UploadOutlined />,
  title,
  description,
  primaryColor,
  variant = "default",
  showPreview = true,
  maxWidth = "100%",
  onImageChange = null,
  accept = null,
  idSync = null,
  onRemove = null,
  simpleButtonOnly = false, // NOVA PROP
  singleFile = false, // NOVA PROP
  tenantId = null, // NOVA PROP para tenant
  ...props 
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isZoomed, setIsZoomed] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const inputRef = useRef(); // Mover para o topo

  // Configurações dinâmicas
  const finalPrimaryColor = primaryColor || theme.primary;
  const finalTitle = title || (variant === "avatar" ? "Alterar foto" : "Clique ou arraste um arquivo");
  const finalDescription = description || (inputType === "images" ? "JPG, PNG ou GIF até 5MB" : "Documentos e planilhas");
  
  // Se for user, usar user.files diretamente (não precisa de preFilesIds)
  const effectivePreFilesIds = useMemo(() => (user ? [] : preFilesIds), [user, preFilesIds]);

  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [uploadedFiles]);

  useEffect(() => {
    // Only fetch files if preFilesIds or user.files have changed, and avoid fetching if files were just removed
    if (user?.id && user?.files) {
      // Handle user files (direct Azure URL)
      const entityLabel = baseUrl === "project" ? (user.userName || 'Projeto') : (user.userName || 'Avatar');
      const filesFromUser = [{
        id: user.files,
        name: entityLabel,
        type: 'image/*',
        preview: user.files,
        uploaded: true,
        lastModified: Date.now(),
        isUserAvatar: baseUrl !== "project",
        isDirectUrl: true
      }];
      setUploadedFiles((prevFiles) => (
        areFilesEquivalent(prevFiles, filesFromUser) ? prevFiles : filesFromUser
      ));
    } else if (user?.id && !user?.files) {
      setUploadedFiles((prevFiles) => (prevFiles.length === 0 ? prevFiles : []));
    } else if (effectivePreFilesIds && (Array.isArray(effectivePreFilesIds) ? effectivePreFilesIds.length > 0 : true)) {
      // Buscar por IDs (tratando URLs diretas também)
      const fileIds = Array.isArray(effectivePreFilesIds) ? effectivePreFilesIds : [effectivePreFilesIds];
      const uniqueFileIds = Array.from(new Set(fileIds.filter(Boolean)));

      const directUrlFiles = [];
      const apiFiles = [];
      uniqueFileIds.forEach(id => {
        if (typeof id === 'string' && id.startsWith('http')) {
          const label = 'arquivo_' + Date.now();
          directUrlFiles.push({
            id,
            name: label,
            type: 'image/*',
            preview: id,
            uploaded: true,
            lastModified: Date.now(),
            isDirectUrl: true
          });
        } else {
          apiFiles.push(id);
        }
      });

      if (directUrlFiles.length > 0) {
        setUploadedFiles((prevFiles) => {
          const allFiles = [...prevFiles, ...directUrlFiles];
          return Array.from(new Map(allFiles.map(file => [file.id, file])).values());
        });
      }

      if (apiFiles.length > 0) {
        (async () => {
          setLoading(true);
          try {
            const responses = await Promise.all(
              apiFiles.map((id) => Api.get(`${baseUrl}/getFile/${id}`, { responseType: 'blob' }))
            );
            const filesFromServer = responses.map((response, index) => {
              const blob = response.data;
              const url = URL.createObjectURL(blob);
              return {
                id: apiFiles[index],
                name: `${apiFiles[index]}`,
                type: blob.type,
                preview: url,
                uploaded: true,
                lastModified: Date.now(),
              };
            });
            setUploadedFiles((prevFiles) => {
              const allFiles = [...prevFiles, ...filesFromServer];
              return Array.from(new Map(allFiles.map(file => [file.id, file])).values());
            });
          } catch (error) {
            message.error("Erro ao carregar arquivos.");
          } finally {
            setLoading(false);
          }
        })();
      }
    }
  }, [effectivePreFilesIds, user?.files, user?.id, baseUrl, user?.userName]); // Removed forceUpdate from dependencies

  const uploadFile = useCallback(async (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("Files", file); // Backend espera "Files" com F maiúsculo
    
    if (userId && baseUrl !== "tenant") {
      // Evitar enviar IdUser quando o upload é para tenant
      formData.append("IdUser", userId);
    }
    if (idSync && baseUrl === "sync") {
      formData.append("IdSync", idSync);
    }
    if (tenantId && baseUrl === "tenant") {
      formData.append("IdTenant", tenantId);
    }
    if (user?.id) {
      // Detectar entidade
      if (baseUrl === "project") {
        formData.append("IdProject", user.id);
      } else if (baseUrl === "tenant") {
        formData.append("IdTenant", tenantId || user.id);
      } else {
        formData.append("IdUser", user.id);
      }
    }

    try {
      const response = await Api.post(`${baseUrl}/uploadFile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        // Suporte para resposta { response: { item1, item2 } }
        let newFileId, integrationId;
        if (response.data.response && response.data.response.item1 && response.data.response.item2) {
          newFileId = response.data.response.item1;
          // Detectar se item2 é MIME type (contém '/') ou ignorar para baseUrl 'sync'
          const candidate = response.data.response.item2;
          const isMimeLike = typeof candidate === 'string' && candidate.includes('/');
          integrationId = (baseUrl !== 'sync' && !isMimeLike) ? candidate : null;
        } else {
          newFileId = response.data.response || response.data;
          integrationId = response.data.integrationId || response.data.id || null;
        }
        if (Array.isArray(newFileId)) {
          newFileId = newFileId[0];
        }

        // Se for URL do Azure Blob, usar diretamente como preview
        const isAzureUrl = typeof newFileId === 'string' && newFileId.startsWith('http');
        const preview = {
          id: newFileId,
          name: file.name, // Usar nome real do arquivo
          preview: isAzureUrl ? newFileId : URL.createObjectURL(file),
          uploaded: true,
          type: file.type,
          isDirectUrl: isAzureUrl
        };

        // Para usuário ou projeto, substituir arquivo existente em vez de adicionar
        if (user?.id) {
          const entityLabel = baseUrl === "project" ? (user.userName || 'Projeto') : (user.userName || 'Avatar');
          const newEntityFile = {
            id: user.id,
            name: entityLabel,
            type: 'image/*',
            preview: preview.preview,
            uploaded: true,
            lastModified: Date.now(),
            isUserAvatar: baseUrl !== "project" // true para usuários, false para projetos
          };
          setUploadedFiles([newEntityFile]);
          if (onFileUpload) {
            if (singleFile) {
              onFileUpload(newFileId, integrationId);
            } else {
              onFileUpload([newFileId], integrationId);
            }
          }
          if (onImageChange) {
            onImageChange(user.id, preview.preview, newFileId);
          }
          setForceUpdate(prev => prev + 1);
        } else {
          setUploadedFiles((prevFiles) => {
            const allFiles = [...prevFiles, preview];
            const uniqueFiles = Array.from(new Map(allFiles.map((file) => [file.id, file])).values());
            // const distinctFileNames = Array.from(new Set(uniqueFiles.map((file) => file.name)));
            if (onFileUpload) {
              if (singleFile) {
                onFileUpload(newFileId, integrationId);
              } else {
                onFileUpload([newFileId], integrationId);
              }
            }
            return uniqueFiles;
          });
        }
        message.success("Arquivo enviado com sucesso!");
      } else {
        message.error("Erro ao enviar o arquivo.");
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error("Erro ao enviar o arquivo.");
    } finally {
      setLoading(false);
    }
  }, [userId, baseUrl, idSync, tenantId, user, onFileUpload, singleFile, onImageChange]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      // Sempre faça uploadFile(file), nunca chame onFileUpload([file]) diretamente
      uploadFile(file);
    }
  }, [uploadFile]);

  const getDefaultAcceptString = () => {
    switch (inputType) {
      case "images":
        return "image/*";
      case "excel":
        return ".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv";
      default:
        return "application/pdf,.doc,.docx,.xls,.xlsx";
    }
  };

  const getInputAcceptTypes = () => {
    if (!accept) {
      return getDefaultAcceptString();
    }

    if (typeof accept === "string") {
      return accept;
    }

    if (Array.isArray(accept)) {
      return accept.join(",");
    }

    if (typeof accept === "object") {
      const parts = [];
      Object.entries(accept).forEach(([mimeType, extensions]) => {
        if (mimeType) parts.push(mimeType);
        if (Array.isArray(extensions)) {
          parts.push(...extensions);
        }
      });
      return parts.join(",");
    }

    return getDefaultAcceptString();
  };

  const getDropzoneAcceptTypes = () => {
    if (accept && typeof accept === "object" && !Array.isArray(accept)) {
      return accept;
    }

    const extensionMimeMap = {
      ".xlsx":
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ".xls": "application/vnd.ms-excel",
      ".csv": "text/csv",
      ".pdf": "application/pdf",
      ".doc": "application/msword",
      ".docx":
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ".png": "image/png",
      ".jpg": "image/jpeg",
      ".jpeg": "image/jpeg",
      ".gif": "image/gif",
      ".webp": "image/webp",
      ".svg": "image/svg+xml",
    };

    const tokens = String(getInputAcceptTypes() || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (tokens.length === 0) {
      return undefined;
    }

    const mappedAccept = {};
    tokens.forEach((token) => {
      if (token.startsWith(".")) {
        const mimeType =
          extensionMimeMap[token.toLowerCase()] || "application/octet-stream";
        if (!mappedAccept[mimeType]) {
          mappedAccept[mimeType] = [];
        }
        mappedAccept[mimeType].push(token);
        return;
      }

      if (token.includes("/")) {
        if (!mappedAccept[token]) {
          mappedAccept[token] = [];
        }
      }
    });

    return Object.keys(mappedAccept).length > 0 ? mappedAccept : undefined;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: getDropzoneAcceptTypes(),
  });

  const handleRemoveFile = async (fileId) => {
    setLoading(true);
    try {
      if (onRemove && typeof onRemove === 'function') {
        await onRemove(fileId);
        setUploadedFiles([]);
        if (onImageChange && user?.id) {
          onImageChange(user.id, null, null);
        }
        message.success("Arquivo removido com sucesso!");
        return;
      }

      if (user?.id) {
        let removeData;
        if (baseUrl === "project") {
          removeData = { idProject: user.id, files: fileId };
        } else if (baseUrl === "tenant") {
          // Remoção para tenant
          removeData = { idTenant: tenantId || user.id, files: fileId };
        } else {
          removeData = { idUser: user.id, files: fileId };
        }

        await Api.post(`${baseUrl}/removeFile`, removeData);
        setUploadedFiles([]);
        
        if (onImageChange) {
          onImageChange(user.id, null, null);
        }
        if (onFileUpload) {
          onFileUpload({ removedFileId: fileId, entityId: user.id });
        }
      } else {
        await Api.post(`${baseUrl}/removeFile/${fileId}`);
        const updatedFiles = uploadedFiles.filter((file) => file.id !== fileId);
        setUploadedFiles(updatedFiles);
        if (onFileUpload) {
          onFileUpload({ removedFileId: fileId });
        }
      }

      message.success("Arquivo removido com sucesso!");
    } catch (error) {
      console.error('Erro ao remover arquivo:', error);
      message.error("Erro ao remover o arquivo.");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (file) => {
    setCurrentPreview(file);
    setPreviewVisible(true);
    setIsZoomed(false); // Reset zoom ao abrir modal
    
    // Calcular dimensões da imagem
    if (file.type.includes("image")) {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height });
      };
      img.src = file.preview;
    }
  };

  // Calcular tamanho ideal do modal baseado na imagem
  const calculateModalSize = () => {
    if (!imageSize.width || !imageSize.height) {
      return { width: "90vw", maxWidth: "800px" };
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const maxModalWidth = Math.min(screenWidth * 0.9, 1200);
    const maxModalHeight = screenHeight * 0.85;

    // Calcular aspect ratio da imagem
    const aspectRatio = imageSize.width / imageSize.height;
    
    // Se a imagem é muito larga
    if (aspectRatio > 2) {
      return { 
        width: `${Math.min(maxModalWidth, imageSize.width + 40)}px`,
        maxWidth: "95vw"
      };
    }
    
    // Se a imagem é muito alta
    if (aspectRatio < 0.5) {
      return { 
        width: `${Math.min(600, imageSize.width + 40)}px`,
        maxWidth: "70vw"
      };
    }
    
    // Imagem com proporção normal
    const idealWidth = Math.min(maxModalWidth, imageSize.width + 40);
    const idealHeight = idealWidth / aspectRatio + 100; // +100 para padding/header
    
    if (idealHeight > maxModalHeight) {
      const adjustedWidth = (maxModalHeight - 100) * aspectRatio + 40;
      return { 
        width: `${Math.max(400, adjustedWidth)}px`,
        maxWidth: "90vw"
      };
    }
    
    return { 
      width: `${Math.max(400, idealWidth)}px`,
      maxWidth: "90vw"
    };
  };

  const modalSize = calculateModalSize();

  // Não mostrar upload se tem arquivos e hideUploadWhenHasFiles é true
  const shouldShowUpload = !(hideUploadWhenHasFiles && uploadedFiles.length > 0);

  // Se simpleButtonOnly, renderizar só o botão
  if (simpleButtonOnly) {
    return (
      <>
        <Button
          type="primary"
          style={{ minWidth: 160, ...style }}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.value = "";
              inputRef.current.click();
            }
          }}
          disabled={loading}
        >
          {loading ? <Loading size="small" tip="" style={{ padding: 0, margin: 0}} /> : 'Escolher arquivo'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={getInputAcceptTypes()}
          style={{ display: 'none' }}
          onChange={e => {
            if (e.target.files && e.target.files[0]) {
              uploadFile(e.target.files[0]);
            }
            e.target.value = "";
          }}
        />
      </>
    );
  }

  // Layout específico para avatar
  if (variant === "avatar") {
    // Se tem arquivos, mostrar com avatar
    if (uploadedFiles.length > 0) {
      return (
        <div className={className} style={{ textAlign: "center", ...style }}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <Avatar
              key={`avatar-${uploadedFiles[0].id}-${forceUpdate}`}
              size={120}
              src={uploadedFiles[0].preview}
              style={{
                border: `3px solid ${theme.white}`,
                boxShadow: `0 8px 24px rgba(0,0,0,0.12)`,
              }}
            >
              {!uploadedFiles[0].preview && (user?.userName || user?.username)?.charAt(0)?.toUpperCase()}
            </Avatar>
            
            <div
              {...getRootProps()}
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                backgroundColor: finalPrimaryColor,
                border: `3px solid ${theme.white}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.backgroundColor = `${finalPrimaryColor}dd`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.backgroundColor = finalPrimaryColor;
              }}
            >
              <input {...getInputProps()} />
              <CameraOutlined style={{ color: theme.white, fontSize: "16px" }} />
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <p style={{ 
              fontSize: "14px", 
              color: theme.textSecondary, 
              margin: "0 0 8px 0",
              fontWeight: 500 
            }}>
              {user?.userName || user?.username}
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              <Button
                type="link"
                icon={<AiFillEye />}
                onClick={() => handlePreview(uploadedFiles[0])}
                style={{ color: finalPrimaryColor, padding: "0 8px" }}
                size="small"
              >
                Visualizar
              </Button>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveFile(uploadedFiles[0].id)}
                style={{ padding: "0 8px" }}
                size="small"
              >
                Remover
              </Button>
            </div>
          </div>

          <Modal
            open={previewVisible}
            onCancel={() => setPreviewVisible(false)}
            centered
            width={modalSize.width}
            style={{ maxWidth: modalSize.maxWidth }}
            footer={null}
            styles={{
              body: { padding: "20px", textAlign: "center" }
            }}
          >
            {currentPreview && (
              <div>
                <div style={{ marginBottom: "12px", textAlign: "right" }}>
                  <Button
                    size="small"
                    onClick={() => setIsZoomed(!isZoomed)}
                    style={{ color: finalPrimaryColor }}
                  >
                    {isZoomed ? "🔍 Normal" : "🔍 Zoom"}
                  </Button>
                </div>
                <div style={{ 
                  maxHeight: "75vh", 
                  overflow: isZoomed ? "auto" : "hidden",
                  border: isZoomed ? `1px solid ${theme.border}` : "none",
                  borderRadius: "8px"
                }}>
                  <img
                    alt="Preview"
                    style={{ 
                      width: isZoomed ? "auto" : "100%",
                      maxWidth: isZoomed ? "none" : "100%",
                      height: isZoomed ? "auto" : "auto",
                      maxHeight: isZoomed ? "none" : "75vh",
                      objectFit: isZoomed ? "none" : "contain",
                      borderRadius: "8px",
                      minHeight: isZoomed ? "auto" : "200px",
                      cursor: isZoomed ? "grab" : "zoom-in",
                      transition: "all 0.3s ease"
                    }}
                    src={currentPreview.preview}
                    onClick={() => setIsZoomed(!isZoomed)}
                  />
                </div>
              </div>
            )}
          </Modal>
        </div>
      );
    } else {
      // Se não tem arquivos, mostrar apenas o avatar padrão com botão de upload
      return (
        <div className={className} style={{ textAlign: "center", ...style }}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <Avatar
              key={`avatar-default-${user?.id}-${forceUpdate}`}
              size={120}
              style={{
                border: `3px solid ${theme.white}`,
                boxShadow: `0 8px 24px rgba(0,0,0,0.12)`,
                backgroundColor: theme.primary,
              }}
            >
              {(user?.userName || user?.username)?.charAt(0)?.toUpperCase()}
            </Avatar>
            
            <div
              {...getRootProps()}
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                backgroundColor: finalPrimaryColor,
                border: `3px solid ${theme.white}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.backgroundColor = `${finalPrimaryColor}dd`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.backgroundColor = finalPrimaryColor;
              }}
            >
              <input {...getInputProps()} />
              <CameraOutlined style={{ color: theme.white, fontSize: "16px" }} />
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <p style={{ 
              fontSize: "14px", 
              color: theme.textSecondary, 
              margin: "0 0 8px 0",
              fontWeight: 500 
            }}>
              {user?.userName || user?.username}
            </p>
            <p style={{
              fontSize: "12px",
              color: theme.textSecondary,
              margin: 0
            }}>
              Clique no ícone para adicionar uma foto
            </p>
          </div>
        </div>
      );
    }
  }

  // Layout padrão melhorado
  const modernDropzoneStyle = {
    border: isDragActive ? `2px dashed ${finalPrimaryColor}` : `2px dashed ${theme.border}`,
    borderRadius: "16px",
    padding: "40px 20px",
    textAlign: "center",
    cursor: "pointer",
    background: isDragActive 
      ? `linear-gradient(135deg, ${finalPrimaryColor}08, ${finalPrimaryColor}15)` 
      : `linear-gradient(135deg, ${theme.white}, ${theme.backgroundLight})`,
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    maxWidth: maxWidth,
    ...style,
  };

  return (
    <div className={className}>
      {shouldShowUpload && (
        <div {...getRootProps()} style={modernDropzoneStyle}>
          <input {...getInputProps()} />
          
          {/* Background decoration */}
          <div style={{
            position: "absolute",
            top: "-50%",
            right: "-50%",
            width: "100%",
            height: "100%",
            background: `radial-gradient(circle, ${finalPrimaryColor}05 0%, transparent 70%)`,
            pointerEvents: "none",
            transition: "all 0.4s ease",
            transform: isDragActive ? "scale(1.5)" : "scale(1)",
          }} />
          
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${finalPrimaryColor}, ${finalPrimaryColor}cc)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              transition: "all 0.3s ease",
              transform: isDragActive ? "scale(1.1) rotate(5deg)" : "scale(1)",
              boxShadow: `0 8px 24px ${finalPrimaryColor}25`,
            }}>
              {React.cloneElement(icon, { 
                style: { 
                  fontSize: "24px", 
                  color: theme.white
                } 
              })}
            </div>

            <h3 style={{
              fontSize: "18px",
              fontWeight: 600,
              color: theme.textSecondary,
              margin: "0 0 8px 0",
              lineHeight: 1.3
            }}>
              {finalTitle}
            </h3>

            <p style={{
              fontSize: "14px",
              color: theme.textSecondary,
              margin: "0 0 16px 0",
              lineHeight: 1.4
            }}>
              {finalDescription}
            </p>

            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "20px",
              backgroundColor: `${finalPrimaryColor}10`,
              border: `1px solid ${finalPrimaryColor}20`,
              fontSize: "12px",
              color: finalPrimaryColor,
              fontWeight: 500
            }}>
              <PlusOutlined style={{ fontSize: "10px" }} />
              {isDragActive ? "Solte aqui" : "Escolher arquivo"}
            </div>
          </div>

          {loading && (
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `${theme.white}f0`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "16px"
            }}>
              <Spin size="large" />
            </div>
          )}
        </div>
      )}

      {showPreview && uploadedFiles.length > 0 && variant !== "avatar" && (
        <div style={{ marginTop: "20px" }}>
          <Row gutter={[16, 16]}>
            {uploadedFiles.map((file) => (
              <Col xs={24} sm={12} md={8} lg={6} key={file.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: `1px solid ${theme.border}`,
                    transition: "all 0.3s ease"
                  }}
                  bodyStyle={{ padding: "12px" }}
                  actions={[
                    <Button
                      type="link"
                      icon={<AiFillEye />}
                      onClick={() => handlePreview(file)}
                      style={{ color: finalPrimaryColor }}
                    >
                      Ver
                    </Button>,
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveFile(file.id)}
                    >
                      Remover
                    </Button>,
                  ]}
                >
                  {file.type.includes("image") ? (
                    <div style={{ textAlign: "center" }}>
                      <img
                        src={file.preview}
                        alt={file.name}
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                        onClick={() => handlePreview(file)}
                      />
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <FileOutlined style={{ 
                        fontSize: "32px", 
                        color: finalPrimaryColor,
                        marginBottom: "8px"
                      }} />
                      <p style={{ margin: 0, fontSize: "12px", wordBreak: "break-word" }}>
                        {file.name}
                      </p>
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        centered
        width={modalSize.width}
        style={{ maxWidth: modalSize.maxWidth }}
        footer={null}
        styles={{
          body: { padding: "20px", textAlign: "center" }
        }}
      >
        {currentPreview && currentPreview.type.startsWith("image") ? (
          <div>
            <div style={{ marginBottom: "12px", textAlign: "right" }}>
              <Button
                size="small"
                onClick={() => setIsZoomed(!isZoomed)}
                style={{ color: finalPrimaryColor }}
              >
                {isZoomed ? "🔍 Normal" : "🔍 Zoom"}
              </Button>
            </div>
            <div style={{ 
              maxHeight: "80vh", 
              overflow: isZoomed ? "auto" : "hidden",
              border: isZoomed ? `1px solid ${theme.border}` : "none",
              borderRadius: "8px"
            }}>
              <img
                alt="Preview"
                style={{ 
                  width: isZoomed ? "auto" : "100%",
                  maxWidth: isZoomed ? "none" : "100%",
                  height: isZoomed ? "auto" : "auto",
                  maxHeight: isZoomed ? "none" : "80vh",
                  objectFit: isZoomed ? "none" : "contain",
                  borderRadius: "8px",
                  minHeight: isZoomed ? "auto" : "200px",
                  cursor: isZoomed ? "grab" : "zoom-in",
                  transition: "all 0.3s ease"
                }}
                src={currentPreview.preview}
                onClick={() => setIsZoomed(!isZoomed)}
              />
            </div>
          </div>
        ) : (
          currentPreview && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <FileOutlined style={{ fontSize: "48px", color: finalPrimaryColor, marginBottom: "16px" }} />
              <p style={{ fontSize: "16px", marginBottom: "16px" }}>{currentPreview.name}</p>
              <Button 
                type="primary" 
                href={currentPreview.preview} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Abrir arquivo
              </Button>
            </div>
          )
        )}
      </Modal>
    </div>
  );
};

FileUpload.propTypes = {
  onFileUpload: PropTypes.func,
  preFilesIds: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  inputType: PropTypes.oneOf(["images", "files", "excel"]),
  style: PropTypes.object,
  className: PropTypes.string,
  userId: PropTypes.string,
  baseUrl: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.string,
    userName: PropTypes.string,
    files: PropTypes.string,
  }),
  hideUploadWhenHasFiles: PropTypes.bool,
  icon: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  primaryColor: PropTypes.string,
  variant: PropTypes.oneOf(["default", "compact", "avatar", "gallery"]),
  showPreview: PropTypes.bool,
  maxWidth: PropTypes.string,
  onImageChange: PropTypes.func,
  accept: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  simpleButtonOnly: PropTypes.bool, // Adicionado propTypes
  singleFile: PropTypes.bool, // Adicionado propTypes
  tenantId: PropTypes.string, // Adicionado propTypes
};

export default FileUpload; 
